import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Link,
} from 'react-router-dom';

import UpdateNoticeLayout
  from '../components/UpdateNoticeLayout';

import {
  deleteUpdateNotice,
  getUpdateNotices,
} from '../services/updateNoticeService';

import {
  formatNoticeDate,
  getNoticeOverview,
} from '../config/updateNoticeConfig';

import useUpdateNoticeLanguage
  from '../hooks/useUpdateNoticeLanguage';

import './UpdateNoticePage.css';

function OverviewLines({
  overview,
}) {
  
  const lines = String(
    overview || '',
  )
    .split(/\r?\n/)
    .map(
      (line) =>
        line.trim(),
    )
    .filter(Boolean);

  if (
    lines.length === 0
  ) {
    return <span>-</span>;
  }

  return (
    <ul>
      {lines
        .slice(0, 3)
        .map(
          (
            line,
            index,
          ) => (
            <li
              key={`${line}-${index}`}
            >
              {line}
            </li>
          ),
        )}
    </ul>
  );
}

export default function UpdateNoticeAdminPage() {
  const {
    noticeLanguage,
  } = useUpdateNoticeLanguage();

  const [
    notices,
    setNotices,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    deletingSeq,
    setDeletingSeq,
  ] = useState(null);

  const loadNotices =
    useCallback(
      async () => {
        setLoading(true);
        setError('');

        try {
          const result =
            await getUpdateNotices(
              noticeLanguage,
            );

          setNotices(
            Array.isArray(result)
              ? result
              : [],
          );
        } catch (loadError) {
          setNotices([]);

          setError(
            loadError?.message
          );
        } finally {
          setLoading(false);
        }
      },
      [noticeLanguage],
    );

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const onDelete = async (
    notice,
  ) => {
    const confirmed =
      window.confirm(
        `“${notice.title || '-'}” 공지를 삭제하시겠습니까?`,
      );

    if (!confirmed) {
      return;
    }

    setDeletingSeq(
      notice.seq,
    );

    try {
      await deleteUpdateNotice(
        notice.seq,
      );

      await loadNotices();
    } catch (deleteError) {
      window.alert(
        deleteError?.message ||
          '공지 삭제에 실패했습니다.',
      );
    } finally {
      setDeletingSeq(null);
    }
  };

  return (
    <UpdateNoticeLayout>
      <div className="updatenotice-section-heading admin">
        <div>
          <span>
            ADMINISTRATION
          </span>

          <h2>
            공지 관리
          </h2>
        </div>

        <Link
          to="/updatenotices/admin/new"
          className="updatenotice-primary-button"
        >
          새 공지 작성
        </Link>
      </div>

      {loading && (
        <div className="updatenotice-state-card">
          공지 목록을 불러오는 중입니다.
        </div>
      )}

      {!loading && error && (
        <div className="updatenotice-state-card error">
          {error}
        </div>
      )}

      {!loading &&
        !error && (
          <div className="updatenotice-table-card">
            <div className="updatenotice-table-scroll">
              <table className="updatenotice-table admin-table">
                <thead>
                  <tr>
                    <th>
                      No.
                    </th>

                    <th>
                      제목
                    </th>

                    <th>
                      프로그램
                    </th>

                    <th>
                      플랫폼
                    </th>

                    <th>
                      버전
                    </th>

                    <th>
                      날짜
                    </th>

                    <th>
                      관리
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {notices.map(
                    (
                      notice,
                      index,
                    ) => (
                      <tr
                        key={
                          `${notice.seq}-${notice.title}-${index}`
                        }
                      >
                        <td>
                          {notice.seq ||
                            index + 1}
                        </td>

                        <td>
                          <Link
                            to={`/updatenotices/${notice.seq}`}
                            className="updatenotice-title-link"
                          >
                            {notice.title ||
                              '-'}
                          </Link>
                          <tr>
                            <div className="updatenotice-overview">
                              <OverviewLines
                                overview={
                                  getNoticeOverview(
                                    notice,
                                  )
                                }
                              />
                            </div>

                          </tr>
                        </td>
                        <td>
                          {notice.program ||
                            '-'}
                        </td>

                        <td>
                          {notice.platform ||
                            '-'}
                        </td>

                        <td>
                          {notice.version ||
                            '-'}
                        </td>

                        <td>
                          {formatNoticeDate(
                            notice.date,
                          )}
                        </td>

                        <td>
                          <div className="updatenotice-admin-actions">
                            <Link
                              to={`/updatenotices/admin/${notice.seq}/edit`}
                            >
                              수정
                            </Link>

                            <button
                              type="button"
                              onClick={() =>
                                onDelete(
                                  notice,
                                )
                              }
                              disabled={
                                deletingSeq ===
                                notice.seq
                              }
                            >
                              {deletingSeq ===
                              notice.seq
                                ? '삭제 중'
                                : '삭제'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}

                  {notices.length ===
                    0 && (
                    <tr>
                      <td colSpan={7}>
                        등록된 공지가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </UpdateNoticeLayout>
  );
}
